import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { AdminKind } from '../admin-hub/admin-hub.component';
import {
  CertificateDownloadService,
  DownloadSessionContext,
  ObtenerCertificadoResponse
} from '../../core/services/certificate-download.service';
import { Ccsw5AuthWindowService } from '../../core/services/ccsw5-auth-window.service';
import {
  ccsw5PasswordValidators,
  passwordHasCcsw5Length,
  passwordHasMinLength,
  passwordHasNumber,
  passwordHasSpecial,
  passwordHasUpper,
  tokenVirtualPasswordValidators,
  PASSWORD_MAX_LENGTH
} from '../../core/validators/password.validators';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { SignMechanismService, SignMechanism } from '../../core/services/sign-mechanism.service';
import { SignStateService } from '../../core/services/sign-state.service';
import { TabStateService } from '../../core/services/tab-state.service';

type DownloadStep = 'entry' | 'otp' | 'acceso' | 'listo';

@Component({
  selector: 'app-admin-download-panel',
  templateUrl: './admin-download-panel.component.html',
  styleUrls: ['./admin-download-panel.component.css'],
  standalone: false
})
export class AdminDownloadPanelComponent implements OnInit, OnDestroy {
  adminKind: AdminKind = 'token-virtual';
  step: DownloadStep = 'entry';
  readonly passwordMaxLength = PASSWORD_MAX_LENGTH;

  ordenIdInput = '';
  loading = false;
  errorMessage = '';

  session: DownloadSessionContext | null = null;
  correoOfuscado = '';

  otpEnviado = false;
  otpValue = '';
  otpHash = '';
  otpError: string | null = null;
  /** Reinicia el temporizador del bloque OTP al reenviar. */
  otpResendKey = 0;

  accesoForm!: FormGroup;
  showPass = false;
  showPassConfirm = false;
  registrando = false;
  esperandoAuth = false;
  emitiendo = false;
  ventanaAuthCerrada = false;

  certificado: ObtenerCertificadoResponse | null = null;

  /** P10: adjunto de llave privada (si el backend lo devuelve). */
  llavePrivadaAdjunto: { mime: string; name: string; data: string } | null = null;
  generandoLlave = false;
  llavePrivadaDescargada = false;

  private readonly destroy$ = new Subject<void>();
  private authSubs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private downloadService: CertificateDownloadService,
    private ccsw5Auth: Ccsw5AuthWindowService,
    private notification: NotificationService,
    private uiHelper: UiHelperService,
    private signMechanism: SignMechanismService,
    private signState: SignStateService,
    private tabState: TabStateService
  ) {
    this.accesoForm = this.fb.group({
      contrasena: ['', ccsw5PasswordValidators()],
      confirmar: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    const parentData = this.route.parent?.snapshot.data ?? {};
    this.adminKind = (parentData['adminKind'] as AdminKind) || 'token-virtual';

    if (!this.esDescargaActiva) {
      return;
    }

    this.configurarValidadoresAcceso();

    const idFromUrl = this.route.snapshot.queryParamMap.get('id');
    if (idFromUrl) {
      this.ordenIdInput = idFromUrl.trim();
      this.iniciarConOrden(idFromUrl.trim());
    }
  }

  ngOnDestroy(): void {
    this.clearAuthSubs();
    this.uiHelper.hideSpinner();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get kindLabel(): string {
    switch (this.adminKind) {
      case 'token-virtual-codigo':
        return 'Certifirma Digital Virtual con código de verificación';
      case 'p10':
        return 'Certifirma Digital PKCS10';
      default:
        return 'Certifirma Digital Virtual';
    }
  }

  /** Descarga activa: TV clásico, TV código o P10. */
  get esDescargaActiva(): boolean {
    return this.esTvDescarga || this.esP10;
  }

  /** Descarga TV (clásico o con código). */
  get esTvDescarga(): boolean {
    return this.adminKind === 'token-virtual' || this.adminKind === 'token-virtual-codigo';
  }

  get esTvCodigo(): boolean {
    return this.adminKind === 'token-virtual-codigo';
  }

  get esTvClasico(): boolean {
    return this.adminKind === 'token-virtual';
  }

  get esP10(): boolean {
    return this.adminKind === 'p10';
  }

  get etiquetaPasoAcceso(): string {
    return this.esP10 ? 'Descarga' : 'Acceso';
  }

  /** Nombre + apellido para saludos (omite vacíos; fallback al nombre completo). */
  get nombreSaludo(): string {
    if (!this.session) {
      return '';
    }
    const corto = [this.session.primerNombre, this.session.primerApellido]
      .map((p) => String(p ?? '').trim())
      .filter((p) => p !== '')
      .join(' ');
    return corto || this.session.nombreCompleto.trim();
  }

  get otpCompleto(): boolean {
    return String(this.otpValue ?? '').replace(/\D/g, '').length === 4;
  }

  get passwordsMatch(): boolean {
    const a = this.accesoForm.get('contrasena')?.value;
    const b = this.accesoForm.get('confirmar')?.value;
    return !!a && a === b;
  }

  /** Muestra el aviso en rojo apenas ambos campos tienen valor y no coinciden. */
  get muestraErrorPassNoCoincide(): boolean {
    const a = String(this.accesoForm.get('contrasena')?.value ?? '');
    const b = String(this.accesoForm.get('confirmar')?.value ?? '');
    return a.length > 0 && b.length > 0 && a !== b;
  }

  /** Cualquier proceso async en curso: bloquea Cancelar y edición concurrente. */
  get procesoEnCurso(): boolean {
    return !!(this.loading || this.registrando || this.esperandoAuth || this.emitiendo || this.generandoLlave);
  }

  get puedeAutenticar(): boolean {
    return this.accesoForm.valid && this.passwordsMatch && !this.registrando && !this.esperandoAuth && !this.emitiendo;
  }

  get hasMinLength(): boolean {
    return passwordHasMinLength(String(this.accesoForm.get('contrasena')?.value ?? ''));
  }

  get hasCcsw5Length(): boolean {
    return passwordHasCcsw5Length(String(this.accesoForm.get('contrasena')?.value ?? ''));
  }
  get hasUpper(): boolean {
    return passwordHasUpper(String(this.accesoForm.get('contrasena')?.value ?? ''));
  }
  get hasNumber(): boolean {
    return passwordHasNumber(String(this.accesoForm.get('contrasena')?.value ?? ''));
  }
  get hasSpecial(): boolean {
    return passwordHasSpecial(String(this.accesoForm.get('contrasena')?.value ?? ''));
  }

  submitOrdenId(): void {
    const id = String(this.ordenIdInput ?? '').trim();
    if (!id) {
      this.errorMessage = 'Ingresa el número de solicitud / orden de pedido.';
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.iniciarConOrden(id);
  }

  private iniciarConOrden(ordenId: string): void {
    this.loading = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner('Consultando orden de pedido…', 'admin');
    const modo = this.esTvCodigo ? 'codigo' : this.esP10 ? 'p10' : 'clasico';
    this.downloadService.consultarOrdenPedido(ordenId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.loading = false;
        this.uiHelper.hideSpinner();
        if (res?.codigo != null && Number(res.codigo) !== 200) {
          this.errorMessage = res.mensaje || 'No fue posible obtener la orden de pedido.';
          return;
        }
        try {
          this.session = this.downloadService.buildDownloadSession(res, ordenId, modo);
          this.correoOfuscado = this.downloadService.ofuscarCorreo(this.session.correo);
          this.step = 'otp';
        } catch (e: unknown) {
          this.errorMessage = e instanceof Error ? e.message : 'No fue posible preparar la descarga.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.uiHelper.hideSpinner();
        this.errorMessage =
          err?.error?.mensaje ||
          (Array.isArray(err?.error?.descripcionErrores)
            ? err.error.descripcionErrores.join('\n')
            : null) ||
          err?.message ||
          'No fue posible consultar la orden.';
      }
    });
  }

  private configurarValidadoresAcceso(): void {
    if (this.esP10) {
      return;
    }
    const passValidators = this.esTvCodigo ? ccsw5PasswordValidators() : tokenVirtualPasswordValidators();
    this.accesoForm.get('contrasena')?.setValidators(passValidators);
    this.accesoForm.get('contrasena')?.updateValueAndValidity({ emitEvent: false });
    this.accesoForm.get('confirmar')?.setValidators([Validators.required]);
    this.accesoForm.get('confirmar')?.updateValueAndValidity({ emitEvent: false });
  }

  enviarOtp(): void {
    if (!this.session) {
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.otpError = null;
    this.uiHelper.showSpinner('Enviando código de verificación…', 'admin');
    this.downloadService.solicitarOtp(this.session.correo).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.loading = false;
        this.uiHelper.hideSpinner();
        if (res.statusCode === 200 && res.OTPSha256) {
          this.otpHash = res.OTPSha256;
          this.otpEnviado = true;
          this.otpValue = '';
          this.otpResendKey++;
          void Swal.fire({
            ...this.notification.getSwalBaseConfig(),
            icon: 'success',
            title: 'Código enviado',
            text: `Enviamos el código a ${this.correoOfuscado}`,
            confirmButtonText: this.notification.swalConfirmButtonText,
            customClass: this.notification.swalCustomClass
          });
        } else {
          this.errorMessage = res.responseText || 'No fue posible enviar el código OTP.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.uiHelper.hideSpinner();
        this.errorMessage = err?.error?.mensaje || err?.message || 'No fue posible enviar el código OTP.';
      }
    });
  }

  onOtpSubmit(event: { code: string }): void {
    this.otpValue = String(event?.code ?? '').replace(/\D/g, '').slice(0, 4);
    void this.validarOtp();
  }

  async validarOtp(): Promise<void> {
    if (!this.otpCompleto || !this.otpHash) {
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.otpError = null;
    this.uiHelper.showSpinner('Validando código de verificación…', 'admin');
    try {
      const hash = await this.downloadService.sha256(this.otpValue);
      this.loading = false;
      this.uiHelper.hideSpinner();
      if (hash !== this.otpHash) {
        this.otpError = 'El código de verificación no es correcto.';
        return;
      }
      this.otpEnviado = false;
      this.configurarValidadoresAcceso();
      this.accesoForm.reset();
      this.step = 'acceso';
    } catch {
      this.loading = false;
      this.uiHelper.hideSpinner();
      this.otpError = 'No fue posible validar el código.';
    }
  }

  autenticarYEmitir(): void {
    if (!this.session || !this.puedeAutenticar) {
      this.accesoForm.markAllAsTouched();
      return;
    }
    const password = String(this.accesoForm.get('contrasena')?.value ?? '');

    if (this.esTvClasico) {
      this.emitirClasico(password);
      return;
    }

    const payload = {
      email: this.session.correo.trim(),
      name: this.session.nombreCompleto.trim(),
      username: this.session.numeroIdentificacion.trim(),
      password
    };

    this.registrando = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner('Registrando usuario…', 'admin');
    this.downloadService.registrarUsuarioCcsw5(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.registrando = false;
        this.uiHelper.hideSpinner();
        if (this.downloadService.esRegistroCcsw5Aceptable(res.status, res.body ?? undefined)) {
          this.abrirAuthYEmitir(password);
        } else {
          this.errorMessage = res.body?.message || 'No fue posible registrar el usuario en CCSW5.';
        }
      },
      error: (err) => {
        this.registrando = false;
        this.uiHelper.hideSpinner();
        if (this.downloadService.esRegistroCcsw5Aceptable(err?.status, err?.error)) {
          this.abrirAuthYEmitir(password);
          return;
        }
        this.errorMessage = err?.error?.message || err?.message || 'No fue posible registrar el usuario en CCSW5.';
      }
    });
  }

  /** P10: genera la llave privada (correo + opcional descarga local). */
  generarLlaveP10(): void {
    if (!this.session || this.generandoLlave) {
      return;
    }
    this.generandoLlave = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner(
      'Esto puede tardar varios segundos. No cierres ni canceles; te avisaremos al terminar.',
      'admin',
      'Generando llave privada'
    );
    this.downloadService.generarLlavePrivada(this.session.solicitudTitularId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.generandoLlave = false;
        this.uiHelper.hideSpinner();
        this.llavePrivadaAdjunto = this.downloadService.extraerAdjuntoLlavePrivada(res);
        this.session = { ...this.session!, existeCSR: true };
        this.llavePrivadaDescargada = false;
        void Swal.fire({
          ...this.notification.getSwalBaseConfig(),
          icon: 'success',
          title: 'Llave privada generada',
          text: 'La llave se envió al correo registrado. También puedes guardarla en este equipo.',
          confirmButtonText: this.notification.swalConfirmButtonText,
          customClass: this.notification.swalCustomClass
        });
      },
      error: (err) => {
        const timedOut =
          String(err?.error?.message ?? err?.message ?? '').toLowerCase().includes('timed out');
        if (timedOut) {
          this.uiHelper.updateSpinnerMessage(
            'La operación sigue en curso. Un momento más, por favor…',
            'admin',
            'Generando llave privada'
          );
          this.generandoLlave = false;
          this.generarLlaveP10();
          return;
        }
        this.generandoLlave = false;
        this.uiHelper.hideSpinner();
        this.errorMessage =
          err?.error?.mensaje ||
          (Array.isArray(err?.error?.descripcionErrores)
            ? err.error.descripcionErrores.join('\n')
            : null) ||
          err?.message ||
          'No fue posible generar la llave privada.';
      }
    });
  }

  descargarLlavePrivadaLocal(): void {
    const adjunto = this.llavePrivadaAdjunto;
    if (!adjunto || this.llavePrivadaDescargada) {
      return;
    }
    const base64 = String(adjunto.data)
      .replace(/^data:[^;]+;base64,/i, '')
      .replace(/\s/g, '');
    const name = adjunto.name?.toLowerCase().endsWith('.key')
      ? adjunto.name
      : `${adjunto.name || 'llave_privada'}.key`;
    this.dispararDescargaBase64(base64, adjunto.mime || 'application/pkcs8', name);
    this.llavePrivadaDescargada = true;
    this.llavePrivadaAdjunto = null;
    this.notification.showDownloadSuccessToast('Llave privada descargada');
  }

  omitirDescargaLlave(): void {
    this.llavePrivadaAdjunto = null;
  }

  /** P10: obtiene y descarga el .cer público. */
  obtenerCerP10(): void {
    if (!this.session || this.emitiendo) {
      return;
    }
    if (!this.session.existeCSR) {
      this.errorMessage = 'Primero debes generar la llave privada.';
      return;
    }
    this.emitiendo = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner('Obteniendo certificado P10…', 'admin');
    this.downloadService
      .obtenerCertificado({
        solicitudId: this.session.solicitudTitularId,
        contrasena: '',
        tipoCertificado: 'P10'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.emitiendo = false;
          this.uiHelper.hideSpinner();
          this.certificado = res;
          if (res.estado === 'FINALIZADO' || res.estado === 'DESCARGA') {
            this.step = 'listo';
            if (res.estado === 'DESCARGA' && res.certificadoB64 && res.tipo !== 'PFX') {
              queueMicrotask(() => this.descargarCerSiAplica());
            }
            return;
          }
          this.errorMessage = res.mensaje || `Estado inesperado: ${res.estado ?? 'desconocido'}`;
        },
        error: (err) => {
          const timedOut =
            String(err?.error?.message ?? err?.message ?? '').toLowerCase().includes('timed out');
          if (timedOut) {
            this.uiHelper.updateSpinnerMessage('La operación sigue en curso. Un momento más…', 'admin', 'Obteniendo certificado P10');
            this.emitiendo = false;
            this.obtenerCerP10();
            return;
          }
          this.emitiendo = false;
          this.uiHelper.hideSpinner();
          this.errorMessage =
            err?.error?.mensaje ||
            (Array.isArray(err?.error?.descripcionErrores)
              ? err.error.descripcionErrores.join('\n')
              : null) ||
            err?.message ||
            'No fue posible obtener el certificado P10.';
        }
      });
  }

  /** TV clásico: obtener certificado solo con contraseña (sin CCSW5 / tokenRse). */
  private emitirClasico(password: string): void {
    if (!this.session) {
      return;
    }
    this.emitiendo = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner('Obteniendo certificado…', 'admin');
    this.downloadService
      .obtenerCertificado({
        solicitudId: this.session.solicitudTitularId,
        contrasena: password,
        tipoCertificado: 'V'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.emitiendo = false;
          this.uiHelper.hideSpinner();
          this.certificado = res;
          if (res.estado === 'FINALIZADO' || res.estado === 'DESCARGA') {
            this.step = 'listo';
            return;
          }
          if (res.estado === 'CONTRASENA') {
            this.errorMessage =
              res.mensaje ||
              'Debes definir o confirmar la contraseña del Certifirma Digital Virtual para continuar.';
            return;
          }
          this.errorMessage = res.mensaje || `Estado inesperado: ${res.estado ?? 'desconocido'}`;
        },
        error: (err) => {
          const timedOut =
            String(err?.error?.message ?? err?.message ?? '').toLowerCase().includes('timed out');
          if (timedOut) {
            this.uiHelper.updateSpinnerMessage('La operación sigue en curso. Un momento más…', 'admin', 'Obteniendo certificado');
            this.emitiendo = false;
            this.emitirClasico(password);
            return;
          }
          this.emitiendo = false;
          this.uiHelper.hideSpinner();
          this.errorMessage =
            err?.error?.mensaje ||
            (Array.isArray(err?.error?.descripcionErrores)
              ? err.error.descripcionErrores.join('\n')
              : null) ||
            err?.message ||
            'No pudimos obtener el certificado. Verifica la contraseña e inténtalo de nuevo.';
        }
      });
  }

  reintentarAuth(): void {
    const password = String(this.accesoForm.get('contrasena')?.value ?? '');
    if (!password) {
      return;
    }
    this.abrirAuthYEmitir(password);
  }

  private abrirAuthYEmitir(password: string): void {
    this.clearAuthSubs();
    this.esperandoAuth = true;
    this.ventanaAuthCerrada = false;
    this.errorMessage = '';
    this.uiHelper.showSpinner(
      'Completa el inicio de sesión en la ventana de CCSW5. No cierres esta pantalla.',
      'admin',
      'Esperando autenticación'
    );

    const subCred = this.ccsw5Auth.credenciales$.pipe(takeUntil(this.destroy$)).subscribe((cred) => {
      if (cred.code && cred.state === '1') {
        this.esperandoAuth = false;
        this.emitirConToken(password, String(cred.code));
      }
    });
    const subCierre = this.ccsw5Auth.ventanaCerrada$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.esperandoAuth && !this.emitiendo) {
        this.esperandoAuth = false;
        this.ventanaAuthCerrada = true;
        this.uiHelper.unlockUi();
      }
    });
    this.authSubs = [subCred, subCierre];
    this.ccsw5Auth.abrirVentanaLoginCcsw5();
  }

  private emitirConToken(password: string, tokenRse: string): void {
    if (!this.session) {
      this.esperandoAuth = false;
      this.uiHelper.unlockUi();
      this.errorMessage = 'La sesión de descarga expiró. Vuelve a consultar la orden.';
      return;
    }
    this.emitiendo = true;
    this.errorMessage = '';
    this.uiHelper.showSpinner('Generando certificado…', 'admin');
    this.downloadService
      .obtenerCertificado({
        solicitudId: this.session.solicitudTitularId,
        contrasena: password,
        tokenRse,
        tipoCertificado: 'V'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.emitiendo = false;
          this.uiHelper.unlockUi();
          this.certificado = res;
          if (res.estado === 'FINALIZADO' || res.estado === 'DESCARGA') {
            this.step = 'listo';
          } else {
            this.errorMessage = res.mensaje || `Estado inesperado: ${res.estado ?? 'desconocido'}`;
          }
        },
        error: (err) => {
          this.emitiendo = false;
          this.uiHelper.unlockUi();
          this.errorMessage =
            err?.error?.mensaje ||
            (Array.isArray(err?.error?.descripcionErrores)
              ? err.error.descripcionErrores.join('\n')
              : null) ||
            err?.message ||
            'No pudimos generar el certificado. Verifica tu autenticación e inténtalo de nuevo.';
        }
      });
  }

  descargarCerSiAplica(): void {
    const c = this.certificado;
    if (!c?.certificadoB64 || c.tipo === 'PFX') {
      return;
    }
    this.dispararDescargaBase64(
      String(c.certificadoB64).replace(/\s/g, ''),
      'application/pkix-cert',
      c.nombreArchivo || 'certificado.cer'
    );
  }

  /** Ir a Firmar con el mecanismo correspondiente al tipo de administración. */
  irAFirmar(): void {
    const mechanism: SignMechanism = this.mecanismoFirmaDesdeAdmin();
    this.signMechanism.setMechanism(mechanism);
    if (mechanism === 'token-virtual') {
      this.signState.setCertificateType('token');
    } else if (mechanism === 'p10') {
      this.signState.setCertificateType('p10');
    }
    this.tabState.setActiveTab('sign');
    void this.router.navigate(['/firmar']);
  }

  /** P10: tras el .cer, continuar en Unir llaves / PFX. */
  irAUnirLlaves(): void {
    void this.router.navigate(['/administrar/certifirma-pkcs10/unir-llaves']);
  }

  private mecanismoFirmaDesdeAdmin(): SignMechanism {
    switch (this.adminKind) {
      case 'token-virtual-codigo':
        return 'rse';
      case 'p10':
        return 'p10';
      default:
        return 'token-virtual';
    }
  }

  /** Vuelve al inicio de Descargar certificado (mismo path, limpia el flujo). */
  reiniciar(): void {
    this.clearAuthSubs();
    this.ccsw5Auth.cerrarVentana();
    this.ccsw5Auth.limpiarAutenticacionEnCurso();
    this.session = null;
    this.certificado = null;
    this.otpValue = '';
    this.otpHash = '';
    this.otpEnviado = false;
    this.otpError = null;
    this.errorMessage = '';
    this.ordenIdInput = '';
    this.llavePrivadaAdjunto = null;
    this.generandoLlave = false;
    this.llavePrivadaDescargada = false;
    this.step = 'entry';
    this.accesoForm.reset();
    this.configurarValidadoresAcceso();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearAuthSubs(): void {
    this.authSubs.forEach((s) => s.unsubscribe());
    this.authSubs = [];
  }

  private dispararDescargaBase64(base64: string, mime: string, fileName: string): void {
    try {
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) {
        bytes[i] = bin.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 90_000);
    } catch {
      const a = document.createElement('a');
      a.href = `data:${mime};base64,${base64}`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
}
